import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  pickBilingual,
  type PortalInvoiceLineView,
  type PortalInvoiceView,
  type PortalStatementEntryView,
} from '@lims/shared-types';
import { ApiError } from '../api/client.js';
import { formatDate, formatMoney } from '../components/format.js';
import {
  Alert,
  DataTable,
  DateField,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
  Panel,
  ReadOnlyField,
  StatusBadge,
  TextField,
  type DataColumn,
} from '../components/ui/index.js';
import { currentLocale } from '../i18n/index.js';
import { portalApi } from './portal-api.js';
import { PORTAL_QUERY_KEY } from './PortalSession.js';

/**
 * The client's invoices and statement — Phase 11, financial visibility only.
 *
 * The navigation hides these pages from a contact without financial
 * visibility, and the API refuses them regardless (403 with
 * `financial_visibility_required`), which is shown here as its own sentence.
 * Every figure is the server's; the portal computes nothing. An invoice here
 * is a financial record, NOT a tax invoice (D160).
 */

const linkClass = 'rounded-sm font-medium text-primary-700 hover:underline';

function isFinanciallyHidden(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.details.some((detail) => detail.code === 'financial_visibility_required')
  );
}

function FinanceError({ error, onRetry }: { readonly error: unknown; readonly onRetry?: () => void }) {
  const { t } = useTranslation();
  if (isFinanciallyHidden(error)) return <Alert tone="warning" title={t('portal.finance.notVisible')} />;
  return <ErrorState error={error} {...(onRetry ? { onRetry } : {})} />;
}

function useMoney() {
  const locale = currentLocale();
  return (minor: number | null | undefined, currency: string) =>
    formatMoney(minor, { locale, currency }) ?? '';
}

export function PortalInvoicesPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const money = useMoney();
  const [page, setPage] = useState(1);

  const list = useQuery({
    queryKey: [...PORTAL_QUERY_KEY, 'invoices', page],
    queryFn: () => portalApi.invoices({ page, pageSize: 25 }),
  });

  const columns: readonly DataColumn<PortalInvoiceView>[] = [
    {
      key: 'number',
      header: t('portal.invoices.number'),
      render: (row) => (
        <Link to={`/portal/invoices/${row.id}`} className={linkClass}>
          <bdi dir="ltr">{row.invoiceNumber}</bdi>
        </Link>
      ),
    },
    {
      key: 'dates',
      header: t('portal.invoices.dates'),
      render: (row) => (
        <span className="text-caption text-default-700">
          {t('portal.invoices.issuedDue', {
            issued: formatDate(row.issueDate, { locale }) ?? '',
            due: formatDate(row.dueDate, { locale }) ?? '',
          })}
        </span>
      ),
    },
    {
      key: 'total',
      header: t('portal.invoices.total'),
      align: 'end',
      render: (row) => <bdi>{money(row.totalMinor, row.currency)}</bdi>,
    },
    {
      key: 'balance',
      header: t('portal.invoices.balance'),
      align: 'end',
      render: (row) => <bdi>{money(row.balanceMinor, row.currency)}</bdi>,
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          <StatusBadge label={t(`status.invoice.${row.status}`)} tone="neutral" />
          {row.paymentStatus ? (
            <StatusBadge label={t(`status.payment.${row.paymentStatus}`)} tone="info" />
          ) : null}
          {row.overdue ? <StatusBadge label={t('portal.invoices.overdue')} tone="warning" /> : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('portal.invoices.title')} subtitle={t('portal.invoices.notTaxInvoice')} />
      {list.error ? (
        <FinanceError error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <DataTable
          label={t('portal.invoices.title')}
          columns={columns}
          rows={list.data?.data ?? []}
          rowKey={(row) => row.id}
          isLoading={list.isLoading}
          emptyContent={t('portal.invoices.none')}
          page={page}
          totalPages={list.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      )}
    </>
  );
}

function Figure({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <ReadOnlyField label={label}>
      <bdi>{children}</bdi>
    </ReadOnlyField>
  );
}

export function PortalInvoicePage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const money = useMoney();
  const { id = '' } = useParams<{ id: string }>();

  const item = useQuery({
    queryKey: [...PORTAL_QUERY_KEY, 'invoice', id],
    queryFn: () => portalApi.invoice(id),
    enabled: id !== '',
  });

  if (item.isLoading) return <LoadingState label={t('common.loading')} />;
  if (item.error || !item.data) return <FinanceError error={item.error} onRetry={() => void item.refetch()} />;
  const invoice = item.data;
  const currency = invoice.currency;

  const lineColumns: readonly DataColumn<PortalInvoiceLineView>[] = [
    { key: 'lineNumber', header: '#', render: (line) => line.lineNumber },
    {
      key: 'description',
      header: t('portal.invoices.description'),
      render: (line) => (
        <div className="min-w-0">
          <span className="block">{pickBilingual(line.description, locale) ?? t('common.notRecorded')}</span>
          {line.workOrderNumber ? (
            <span className="block text-caption text-default-600">
              <bdi dir="ltr">{line.workOrderNumber}</bdi>
            </span>
          ) : null}
        </div>
      ),
    },
    { key: 'quantity', header: t('portal.invoices.quantity'), align: 'end', render: (line) => <bdi dir="ltr">{line.quantity}</bdi> },
    { key: 'unitPrice', header: t('portal.invoices.unitPrice'), align: 'end', render: (line) => <bdi>{money(line.unitPriceMinor, currency)}</bdi> },
    { key: 'discount', header: t('portal.invoices.discount'), align: 'end', render: (line) => <bdi>{money(line.discountMinor, currency)}</bdi> },
    { key: 'lineTotal', header: t('portal.invoices.lineTotal'), align: 'end', render: (line) => <bdi>{money(line.totalMinor, currency)}</bdi> },
  ];

  return (
    <>
      <PageHeader
        title={invoice.invoiceNumber}
        subtitle={pickBilingual(invoice.laboratory.name, locale) ?? undefined}
        breadcrumbs={[{ label: t('portal.nav.invoices'), to: '/portal/invoices' }, { label: invoice.invoiceNumber }]}
      />
      <div className="flex flex-col gap-gutter">
        <Alert tone="info" title={t('portal.invoices.notTaxInvoice')} />
        <Panel title={t('portal.invoices.summary')}>
          <div className="grid gap-stack sm:grid-cols-3">
            <Figure label={t('common.status')}>{t(`status.invoice.${invoice.status}`)}</Figure>
            <Figure label={t('portal.invoices.paymentStatus')}>
              {invoice.paymentStatus ? t(`status.payment.${invoice.paymentStatus}`) : t('common.notRecorded')}
            </Figure>
            <Figure label={t('portal.invoices.dueDate')}>{formatDate(invoice.dueDate, { locale }) ?? ''}</Figure>
            <Figure label={t('portal.invoices.subtotal')}>{money(invoice.totals.subtotalMinor, currency)}</Figure>
            <Figure label={t('portal.invoices.discountTotal')}>
              {money(invoice.totals.lineDiscountMinor + invoice.totals.documentDiscountMinor, currency)}
            </Figure>
            <Figure label={t('portal.invoices.tax', { rate: invoice.taxRatePercent })}>
              {money(invoice.totals.taxMinor, currency)}
            </Figure>
            <Figure label={t('portal.invoices.total')}>{money(invoice.totals.totalMinor, currency)}</Figure>
            <Figure label={t('portal.invoices.balance')}>{money(invoice.balanceMinor, currency)}</Figure>
          </div>
          {invoice.clientNote ? (
            <p className="mt-stack whitespace-pre-wrap text-body text-default-700">
              <bdi>{invoice.clientNote}</bdi>
            </p>
          ) : null}
        </Panel>

        <Panel title={t('portal.invoices.lines')}>
          <DataTable
            label={t('portal.invoices.lines')}
            columns={lineColumns}
            rows={invoice.lines}
            rowKey={(line) => line.lineNumber}
            emptyContent={t('portal.invoices.noLines')}
          />
        </Panel>

        <Panel title={t('portal.invoices.payments')}>
          {invoice.payments.length === 0 && invoice.adjustments.length === 0 ? (
            <p className="text-body text-default-600">{t('portal.invoices.noPayments')}</p>
          ) : (
            <ul className="flex flex-col gap-1 text-body text-default-700">
              {invoice.payments.map((payment, index) => (
                <li key={`p${index}`}>
                  {formatDate(payment.receivedOn, { locale })} · {t(`paymentMethod.${payment.method}`)}
                  {payment.reference ? (
                    <>
                      {' · '}
                      <bdi dir="ltr">{payment.reference}</bdi>
                    </>
                  ) : null}
                  {' · '}
                  <bdi>{money(payment.amountMinor, currency)}</bdi>
                </li>
              ))}
              {invoice.adjustments.map((adjustment, index) => (
                <li key={`a${index}`}>
                  {formatDate(adjustment.effectiveOn, { locale })} · {t(`adjustmentKind.${adjustment.kind}`)}
                  {' · '}
                  <bdi>{money(adjustment.amountMinor, currency)}</bdi>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}

export function PortalStatementPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const money = useMoney();
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [currencyFilter, setCurrencyFilter] = useState('');

  const statement = useQuery({
    queryKey: [...PORTAL_QUERY_KEY, 'statement', from, to, currencyFilter],
    queryFn: () =>
      portalApi.statement({
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(currencyFilter.trim() ? { currency: currencyFilter.trim().toUpperCase() } : {}),
      }),
  });

  const record = statement.data;
  const currency = record?.currency ?? '';

  // A position of its own: two movements may share a date, kind and document.
  type Row = PortalStatementEntryView & { readonly position: number };
  const rows: readonly Row[] = (record?.rows ?? []).map((row, position) => ({ ...row, position }));

  const columns: readonly DataColumn<Row>[] = [
    {
      key: 'date',
      header: t('statements.entryDate'),
      render: (row) => <span className="whitespace-nowrap">{formatDate(row.entryDate, { locale })}</span>,
    },
    { key: 'kind', header: t('statements.kindLabel'), render: (row) => t(`accountEntryKind.${row.kind}`) },
    {
      key: 'document',
      header: t('statements.document'),
      render: (row) => <bdi dir="ltr">{row.documentNumber ?? t('common.notRecorded')}</bdi>,
    },
    { key: 'debit', header: t('statements.debit'), align: 'end', render: (row) => (row.debitMinor ? <bdi>{money(row.debitMinor, currency)}</bdi> : null) },
    { key: 'credit', header: t('statements.credit'), align: 'end', render: (row) => (row.creditMinor ? <bdi>{money(row.creditMinor, currency)}</bdi> : null) },
    { key: 'balance', header: t('statements.runningBalance'), align: 'end', render: (row) => <bdi>{money(row.balanceMinor, currency)}</bdi> },
  ];

  if (statement.isLoading) {
    return (
      <>
        <PageHeader title={t('portal.statement.title')} subtitle={t('portal.statement.subtitle')} />
        <LoadingState label={t('common.loading')} />
      </>
    );
  }
  if (statement.error || !record) {
    return (
      <>
        <PageHeader title={t('portal.statement.title')} subtitle={t('portal.statement.subtitle')} />
        <FinanceError error={statement.error} onRetry={() => void statement.refetch()} />
      </>
    );
  }

  return (
    <>
      <PageHeader title={t('portal.statement.title')} subtitle={t('portal.statement.subtitle')} />
      <FilterBar>
        <DateField label={t('statements.from')} value={from} onChange={setFrom} />
        <DateField label={t('statements.to')} value={to} onChange={setTo} />
        <TextField
          label={t('statements.currency')}
          description={t('statements.currencyHint')}
          value={currencyFilter}
          onChange={setCurrencyFilter}
        />
      </FilterBar>
      <div className="flex flex-col gap-gutter">
        {record.defaultsApplied.length > 0 ? (
          <Alert
            tone="info"
            title={t('portal.statement.defaultsApplied', {
              from: formatDate(record.from, { locale }) ?? '',
              to: formatDate(record.to, { locale }) ?? '',
              currency: record.currency,
            })}
          />
        ) : null}
        <Panel title={t('portal.statement.period', {
          from: formatDate(record.from, { locale }) ?? '',
          to: formatDate(record.to, { locale }) ?? '',
        })}>
          <div className="grid gap-stack sm:grid-cols-3">
            <Figure label={t('statements.openingBalance')}>{money(record.openingBalanceMinor, currency)}</Figure>
            <Figure label={t('statements.closingBalance')}>{money(record.closingBalanceMinor, currency)}</Figure>
            <Figure label={t('statements.asOf')}>{formatDate(record.asOf, { locale }) ?? ''}</Figure>
          </div>
        </Panel>
        <DataTable
          label={t('statements.movements')}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.position}
          emptyContent={t('statements.noMovements')}
        />
      </div>
    </>
  );
}
